import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Box,
	Button,
	FormControl,
	Grid,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { DARK_MODE, SKIP_PART_REASONS } from '../../constants';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import ModalDialog from '../ModalDialog/ModalDialog';
import { useResolutionScale } from '../../hooks/useResolutionScale';
import { useSelector } from 'react-redux';

const classNames = {
	content: {
		width: '100%',
		padding: 4,
		borderRadius: 2,
	},
	heading: {
		color: '#FFA222',
		fontSize: pxToRem(12),
		...responsiveFontSizes({ sm: 12, md: 14, lg: 18 }),
		fontWeight: 600,
		fontFamily: "'Uni Neue', sans-serif",
	},
	text: {
		letterSpacing: 0.5,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontWeight: 'bold',
		fontSize: pxToRem(15),
		...responsiveFontSizes({ sm: 15, md: 17, lg: 21 }),
		fontFamily: "'Uni Neue', sans-serif",
	},
	item: {
		paddingTop: '0.3rem',
		paddingBottom: '0.3rem',
	},
	actionBtnContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 2,
		width: '100%',
		marginTop: 4,
		paddingRight: 4,
	},
	cancelBtn: {
		color: '#4D93E3',
		fontWeight: 600,
		fontSize: pxToRem(16),
		fontFamily: "'Uni Neue', sans-serif",
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.1)',
		},
	},
};

const CssSelectDropdown = styled(FormControl)(({ theme }) => ({
	'& .MuiOutlinedInput-root': {
		backgroundColor: theme.palette.mode === DARK_MODE ? 'rgba(255, 255, 255, 0.05)' : '#E8F5FF',
		borderRadius: '8px',
		'& fieldset': {
			border: 'none',
		},
		'&:hover fieldset': {
			border: 'none',
		},
		'&.Mui-focused fieldset': {
			border: 'none',
		},
	},
	'& .MuiSelect-select': {
		padding: '12px 16px',
		fontSize: pxToRem(18),
		fontFamily: "'Uni Neue', sans-serif",
	},
}));

const CssTextField = styled(TextField)(({ theme }) => ({
	'& .MuiOutlinedInput-root': {
		backgroundColor: theme.palette.mode === DARK_MODE ? 'rgba(255, 255, 255, 0.05)' : '#E8F5FF',
		borderRadius: '8px',
		'& fieldset': {
			border: 'none',
		},
		'&:hover fieldset': {
			border: 'none',
		},
		'&.Mui-focused fieldset': {
			border: 'none',
		},
	},
	'& .MuiInputBase-input': {
		fontSize: pxToRem(16),
		fontFamily: "'Uni Neue', sans-serif",
	},
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
	padding: '0.8rem 2.5rem',
	fontSize: pxToRem(16),
	fontWeight: 600,
	fontFamily: "'Uni Neue', sans-serif",
	borderRadius: '8px',
	background: 'linear-gradient(90deg, #0F107B 0%, #1517D0 100%)',
	color: '#fff',
	'&:hover': {
		background: 'linear-gradient(90deg, #0A0A5A 0%, #0F107B 100%)',
	},
	'&.Mui-disabled': {
		color: 'rgba(255, 255, 255, 0.5)',
		backgroundColor: theme.palette.mode === DARK_MODE ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
		background: 'none',
	},
}));

const MarkAsUnprocessed = ({ partDetails, handleSubmit, handleClose }) => {
	const [reason, setReason] = useState(SKIP_PART_REASONS[0]);
	const [remarks, setRemarks] = useState('');
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const isDark = themeMode === DARK_MODE;
	const { scaleValue } = useResolutionScale();

	const {
		serialNumber,
		sequenceNumber,
		parentPartNumber,
		parentPartDescription,
	} = partDetails;

	const isOther = reason === 'OTHER';
	const isConfirmDisabled = isOther && !remarks.trim();

	return (
		<ModalDialog
			open={true}
			title="Take Out"
			onClose={handleClose}
			modalWidth={scaleValue(720)}
			modalHeight="auto"
		>
			<Box sx={classNames.content}>
				<Grid container direction="column" spacing={3}>
					<Grid item>
						<Grid container spacing={5}>
							<Grid item>
								<Typography sx={{ ...classNames.heading, ...classNames.item }}>
									Vin
								</Typography>
								<Typography sx={{ ...classNames.text, ...classNames.item }}>
									{serialNumber}
								</Typography>
							</Grid>
							<Grid item>
								<Typography sx={{ ...classNames.heading, ...classNames.item }}>
									ASN
								</Typography>
								<Typography sx={{ ...classNames.text, ...classNames.item }}>
									{sequenceNumber}
								</Typography>
							</Grid>
							<Grid item>
								<Typography sx={{ ...classNames.heading, ...classNames.item }}>
									VC Number
								</Typography>
								<Typography sx={{ ...classNames.text, ...classNames.item }}>
									{parentPartNumber}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
					{parentPartDescription && (
						<Grid item>
							<Typography sx={{ ...classNames.heading, ...classNames.item }}>
								VC Description
							</Typography>
							<Typography sx={{ ...classNames.text, ...classNames.item }}>
								{parentPartDescription}
							</Typography>
						</Grid>
					)}
					<Grid item container direction="column" spacing={1}>
						<Grid item>
							<Typography sx={{ ...classNames.heading, ...classNames.item }}>
								Reason to mark as unprocessed
							</Typography>
						</Grid>
						<Grid item>
							<CssSelectDropdown fullWidth>
								<Select
									value={reason}
									IconComponent={(props) => (
										<ExpandMoreIcon
											sx={{ fontSize: scaleValue(30), fill: '#FFA222' }}
											{...props}
										/>
									)}
									onChange={(e) => {
										setReason(e.target.value);
										if (e.target.value !== 'OTHER') setRemarks('');
									}}
								>
									{SKIP_PART_REASONS.map((r, index) => (
										<MenuItem key={index} value={r}>
											{r}
										</MenuItem>
									))}
								</Select>
							</CssSelectDropdown>
						</Grid>
					</Grid>
					<Grid item container direction="column" spacing={1}>
						<Grid item>
							<Typography sx={{ ...classNames.heading, ...classNames.item }}>
								Remarks{isOther && ' *'}
							</Typography>
						</Grid>
						<Grid item>
							<CssTextField
								fullWidth
								multiline
								minRows={3}
								maxRows={5}
								placeholder={isOther ? 'Please enter remarks (required)' : 'Enter additional remarks (optional)'}
								value={remarks}
								onChange={(e) => {
									const filtered = e.target.value.replace(/[^a-zA-Z0-9 \n]/g, '');
									setRemarks(filtered);
								}}
							/>
						</Grid>
					</Grid>
				</Grid>

				<Box sx={classNames.actionBtnContainer}>
					<Button sx={classNames.cancelBtn} onClick={handleClose}>
						CANCEL
					</Button>
					<ConfirmButton
						variant="contained"
						disabled={isConfirmDisabled}
						onClick={() => handleSubmit(reason, remarks)}
					>
						CONFIRM
					</ConfirmButton>
				</Box>
			</Box>
		</ModalDialog>
	);
};

export default MarkAsUnprocessed;
